/**
 * Campaign outbox — split into two pages:
 *  - CampaignsList: this channel's campaigns (template, dates, counts).
 *  - CampaignDetail: one campaign's delivery counts + per-recipient status, with a
 *    recipient detail modal and retry/resend.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { IconRefresh, IconReload, IconEye } from '@tabler/icons-react';
import { Stack, Group, Text, Title, Badge, Button, Loader, Alert, ActionIcon, Divider, Modal, SimpleGrid, Table, ScrollArea, Code } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import { toast } from '../../../../components/ui/providers/AlertProvider';
import { useCampaigns, type CampaignListItem, type CampaignView, type CampaignRecipientView } from './useCampaigns';
import { CHANNEL_IDS } from './useCommunicationSettings';
import { useMemberLookup } from './useMemberLookup';
import { HtmlEmailPreview } from './HtmlEmailPreview';
import { normalizeEmailHtml } from './emailHtml';
import { decodeMessageBody } from './decodeBody';

type Channel = 'email' | 'sms' | 'whatsapp' | 'push';

const CAMPAIGN_STATUS: Record<number, { label: string; color: string }> = {
  0: { label: 'Draft', color: 'gray' }, 1: { label: 'Scheduled', color: 'blue' }, 2: { label: 'Sending', color: 'cyan' },
  3: { label: 'Paused', color: 'orange' }, 4: { label: 'Completed', color: 'green' }, 5: { label: 'Failed', color: 'red' },
  [-1]: { label: 'Cancelled', color: 'red' },
};
const RCPT_STATUS: Record<number, { label: string; color: string }> = {
  0: { label: 'Pending', color: 'gray' }, 1: { label: 'Queued', color: 'blue' }, 2: { label: 'Sent', color: 'cyan' },
  3: { label: 'Delivered', color: 'green' }, 4: { label: 'Read', color: 'teal' }, 5: { label: 'Failed', color: 'red' },
  [-1]: { label: 'Skipped', color: 'orange' },
};
const CHANNEL_LABEL = ['Email', 'SMS', 'WhatsApp', 'Push'];

function fmtDate(ymd?: number | null): string {
  if (!ymd) return '—';
  const s = String(Math.trunc(ymd)).padStart(14, '0');
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
}

function friendlyReason(raw?: string | null): string {
  if (!raw) return '';
  try {
    const o = JSON.parse(raw);
    let m: any = o.Message ?? o.message ?? raw;
    if (typeof m === 'string') { try { const inner = JSON.parse(m); m = inner?.error?.message ?? inner?.message ?? m; } catch { /* keep */ } }
    return typeof m === 'string' ? m : raw;
  } catch { return raw; }
}

function prettyRaw(raw?: string | null): string {
  if (!raw) return '';
  try {
    const o: any = JSON.parse(raw);
    if (typeof o.Message === 'string') { try { o.Message = JSON.parse(o.Message); } catch { /* keep */ } }
    return JSON.stringify(o, null, 2);
  } catch { return raw; }
}

function Kpi({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Card withBorder padding="sm">
      <Text size="xs" c="dimmed" tt="uppercase">{label}</Text>
      <Title order={4} c={color}>{value}</Title>
    </Card>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 110 }}>
      <Text size="xs" c="dimmed" tt="uppercase" mb={4}>{label}</Text>
      {children}
    </div>
  );
}

// ============================ LIST ============================
export function CampaignsList({ channel, onOpen }: { channel: Channel; onOpen: (campaignId: number) => void }) {
  const channelId = CHANNEL_IDS[channel];
  const { listCampaigns } = useCampaigns();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setCampaigns(await listCampaigns(channelId)); }
    catch (err: any) { setError(err?.message || 'Failed to load campaigns'); }
    finally { setLoading(false); }
  }, [listCampaigns, channelId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{campaigns.length} campaign(s)</Text>
        <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={load} loading={loading}>Refresh</Button>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      {loading && campaigns.length === 0 ? (
        <Group justify="center" p="xl"><Loader size="lg" /></Group>
      ) : campaigns.length === 0 ? (
        <Card withBorder padding="xl"><Text size="sm" c="dimmed">No campaigns yet. Create one from the Bulk Send page.</Text></Card>
      ) : (
        <Stack gap="xs">
          {campaigns.map((c) => {
            const outstanding = Math.max(0, c.totalRecipients - c.sentCount - c.failedCount);
            return (
              <Card key={c.campaignId} withBorder padding="sm" style={{ cursor: 'pointer' }} onClick={() => onOpen(c.campaignId)}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap="sm" wrap="nowrap">
                      <Text size="sm" fw={500} truncate>{c.name}</Text>
                      <Badge size="xs" variant="light" color={CAMPAIGN_STATUS[c.statusId]?.color}>{CAMPAIGN_STATUS[c.statusId]?.label ?? c.statusId}</Badge>
                    </Group>
                    <Text size="9px" c="dimmed">{fmtDate(c.createdDateTime)}{c.templateName ? ` · ${c.templateName}` : ''}</Text>
                  </Stack>
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Badge size="sm" variant="light" color="cyan">{c.sentCount} sent</Badge>
                    {c.failedCount > 0 && <Badge size="sm" variant="light" color="red">{c.failedCount} failed</Badge>}
                    {outstanding > 0 && <Badge size="sm" variant="light" color="orange">{outstanding} outstanding</Badge>}
                    <Badge size="sm" variant="light" color="gray">{c.totalRecipients} total</Badge>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

// ============================ DETAIL ============================
export function CampaignDetail({ campaignId }: { campaignId: number }) {
  const { getCampaign, getRecipients, retryRecipient } = useCampaigns();
  const { resolve: resolveMembers, lookup: memberLookup } = useMemberLookup();
  const [detail, setDetail] = useState<CampaignView | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipientView[]>([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<Set<number>>(new Set());
  const [recipientDetail, setRecipientDetail] = useState<CampaignRecipientView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([getCampaign(campaignId), getRecipients(campaignId)]);
      setDetail(c);
      setRecipients(r);
    } finally { setLoading(false); }
  }, [getCampaign, getRecipients, campaignId]);

  useEffect(() => { load(); }, [load]);

  // Resolve member name/email for recipient numbers (phone channels) in one batch.
  useEffect(() => { resolveMembers(recipients.map((r) => r.toAddress)); }, [recipients, resolveMembers]);

  const setRowRetrying = (id: number, on: boolean) =>
    setRetrying((prev) => { const next = new Set(prev); on ? next.add(id) : next.delete(id); return next; });

  const handleRetry = async (recipientId: number) => {
    setRowRetrying(recipientId, true);
    try {
      const updated = await retryRecipient(campaignId, recipientId);
      if (updated) {
        setRecipients((prev) => prev.map((r) => (r.recipientId === recipientId ? updated : r)));
        const refreshed = await getCampaign(campaignId);
        if (refreshed) setDetail(refreshed);
        toast.success(updated.statusId === 5 ? `Retry failed: ${friendlyReason(updated.failureReason)}` : 'Recipient resent');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Retry failed');
    } finally {
      setRowRetrying(recipientId, false);
    }
  };

  const handleRetryAllFailed = async () => {
    const failed = recipients.filter((r) => r.statusId === 5);
    for (const r of failed) {
      // eslint-disable-next-line no-await-in-loop
      await handleRetry(r.recipientId);
    }
  };

  if (loading && !detail) return <Group justify="center" p="xl"><Loader size="lg" /></Group>;
  if (!detail) return <Alert color="red">Campaign not found.</Alert>;

  const outstanding = Math.max(0, detail.totalRecipients - detail.sentCount - detail.failedCount);
  const failedCount = recipients.filter((r) => r.statusId === 5).length;

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Title order={4} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail.name}</Title>
          <Badge variant="light" color={CAMPAIGN_STATUS[detail.statusId]?.color}>{CAMPAIGN_STATUS[detail.statusId]?.label ?? detail.statusId}</Badge>
        </Group>
        <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={load}>Refresh</Button>
      </Group>

      {/* Metadata strip — full width, no empty space */}
      <Card withBorder padding="md">
        <Group grow align="flex-start" gap="xl">
          <Meta label="Channel"><Badge size="sm" variant="light">{CHANNEL_LABEL[detail.channelId] ?? detail.channelId}</Badge></Meta>
          <Meta label="Template">
            {detail.templateName
              ? <Text size="sm" ff="monospace">{detail.templateName}{detail.templateLanguage ? ` (${detail.templateLanguage})` : ''}</Text>
              : <Text size="sm" c="dimmed">Free text</Text>}
          </Meta>
          <Meta label="Created"><Text size="sm">{fmtDate(detail.createdDateTime)}</Text></Meta>
          {detail.scheduledDateTime ? <Meta label="Scheduled"><Text size="sm">{fmtDate(detail.scheduledDateTime)}</Text></Meta> : null}
          {detail.subject ? <Meta label="Subject"><Text size="sm">{detail.subject}</Text></Meta> : null}
        </Group>
      </Card>

      {detail.messageBody && (
        <Card withBorder padding="md">
          <Text size="xs" c="dimmed" tt="uppercase" mb={4}>Message</Text>
          {detail.channelId === CHANNEL_IDS.email ? (
            <HtmlEmailPreview html={normalizeEmailHtml(decodeMessageBody(detail.messageBody))} height={320} />
          ) : (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} lineClamp={6}>{detail.messageBody}</Text>
          )}
        </Card>
      )}

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
        <Kpi label="Total" value={detail.totalRecipients} />
        <Kpi label="Sent/Queued" value={detail.sentCount} color="cyan" />
        <Kpi label="Delivered" value={detail.deliveredCount} color="green" />
        <Kpi label="Read" value={detail.readCount} color="teal" />
        <Kpi label="Failed" value={detail.failedCount} color="red" />
        <Kpi label="Outstanding" value={outstanding} color="orange" />
      </SimpleGrid>

      <Card withBorder padding="sm">
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>Recipients ({recipients.length})</Text>
          {failedCount > 0 && (
            <Button size="xs" variant="light" color="red" leftSection={<IconReload size={14} />} onClick={handleRetryAllFailed} loading={retrying.size > 0}>
              Retry all failed ({failedCount})
            </Button>
          )}
        </Group>
        <Divider mb="xs" />
        {loading ? (
          <Group justify="center" p="md"><Loader /></Group>
        ) : (
          <ScrollArea h={400}>
            <Table stickyHeader fz="xs" verticalSpacing={6} variant="hoverable">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Recipient</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Sent</Table.Th>
                  <Table.Th>Reason</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recipients.map((r) => {
                  const member = memberLookup(r.toAddress);
                  const name = member?.name || r.contactName;
                  return (
                  <Table.Tr key={r.recipientId} style={{ cursor: 'pointer' }} onClick={() => setRecipientDetail(r)}>
                    <Table.Td>
                      <Text size="xs">{r.toAddress}</Text>
                      {name && <Text size="9px" c="dimmed">{name}{member?.isMember ? ' · Member' : ''}</Text>}
                      {member?.email && <Text size="9px" c="dimmed">{member.email}</Text>}
                    </Table.Td>
                    <Table.Td><Badge size="xs" variant="light" color={RCPT_STATUS[r.statusId]?.color}>{RCPT_STATUS[r.statusId]?.label ?? r.statusId}</Badge></Table.Td>
                    <Table.Td c="dimmed">{fmtDate(r.sentDateTime)}</Table.Td>
                    <Table.Td c="red"><Text size="xs" c="red" lineClamp={1} style={{ maxWidth: 220 }}>{friendlyReason(r.failureReason)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap={2} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                        <ActionIcon size="sm" variant="subtle" title="View details" onClick={() => setRecipientDetail(r)}><IconEye size={15} /></ActionIcon>
                        <ActionIcon size="sm" variant="subtle" title={r.statusId === 5 ? 'Retry' : 'Resend'} loading={retrying.has(r.recipientId)} onClick={() => handleRetry(r.recipientId)}><IconReload size={15} /></ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>

      <Modal opened={recipientDetail !== null} onClose={() => setRecipientDetail(null)} title="Recipient details" size="lg" centered>
        {recipientDetail && (
          <Stack gap="sm">
            <Group justify="space-between"><Text size="sm" c="dimmed">Recipient</Text><Text size="sm" ff="monospace">{recipientDetail.toAddress}</Text></Group>
            {(() => {
              const member = memberLookup(recipientDetail.toAddress);
              const name = member?.name || recipientDetail.contactName;
              return (
                <>
                  {name && <Group justify="space-between"><Text size="sm" c="dimmed">Contact</Text><Text size="sm">{name}{member?.isMember ? ' (Member)' : ''}</Text></Group>}
                  {member?.email && <Group justify="space-between"><Text size="sm" c="dimmed">Email</Text><Text size="sm">{member.email}</Text></Group>}
                </>
              );
            })()}
            <Group justify="space-between"><Text size="sm" c="dimmed">Status</Text><Badge variant="light" color={RCPT_STATUS[recipientDetail.statusId]?.color}>{RCPT_STATUS[recipientDetail.statusId]?.label ?? recipientDetail.statusId}</Badge></Group>
            <Group justify="space-between"><Text size="sm" c="dimmed">Sent</Text><Text size="sm">{fmtDate(recipientDetail.sentDateTime)}</Text></Group>
            {recipientDetail.messageId != null && <Group justify="space-between"><Text size="sm" c="dimmed">Message ID</Text><Text size="sm" ff="monospace">{recipientDetail.messageId}</Text></Group>}

            {recipientDetail.failureReason && (
              <>
                <Divider label="Failure reason" labelPosition="left" />
                <Alert color="red">{friendlyReason(recipientDetail.failureReason)}</Alert>
                <Text size="xs" c="dimmed">Raw provider response</Text>
                <ScrollArea h={180}><Code block style={{ fontSize: 11 }}>{prettyRaw(recipientDetail.failureReason)}</Code></ScrollArea>
              </>
            )}

            <Group justify="flex-end" mt="xs">
              {recipientDetail.statusId === 5 && (
                <Button variant="light" leftSection={<IconReload size={14} />} loading={retrying.has(recipientDetail.recipientId)} onClick={async () => { await handleRetry(recipientDetail.recipientId); setRecipientDetail(null); }}>
                  Retry
                </Button>
              )}
              <Button variant="default" onClick={() => setRecipientDetail(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
